import { CARS } from './catalog.js';
import { SCENARIOS } from './race-stories.js';
import { STARTING_GRID, STRAIGHT_LENGTH, CURVE_RADIUS, TRACK_LENGTH } from './track.js';

const clamp = value => Math.max(0, Math.min(1, value));
const smooth = value => value * value * (3 - 2 * value);
const runningLanes = [-5.4, 5.4, -2.7, 2.7, 0];
const runningOffsets = [0, -3.6, -8.2, -13.2, -19];
const curveEnd = STRAIGHT_LENGTH + Math.PI * CURVE_RADIUS;

function progress(story, time) {
  if (time > story.duration) {
    const speed = 60 * Math.exp(-(time - story.duration) / .8);
    return { distance: TRACK_LENGTH + 48 * (1 - Math.exp(-(time - story.duration) / .8)), speed, acceleration: -speed / .8 };
  }
  let index = 1;
  while (index < story.motionKeys.length - 1 && time > story.motionKeys[index][0]) index++;
  const [start, distance, startSpeed] = story.motionKeys[index - 1];
  const [end, endDistance, endSpeed] = story.motionKeys[index];
  const duration = end - start, u = (time - start) / duration, travel = endDistance - distance;
  const a = 3 * travel - duration * (2 * startSpeed + endSpeed);
  const b = -2 * travel + duration * (startSpeed + endSpeed);
  return {
    distance: distance + duration * startSpeed * u + a * u * u + b * u ** 3,
    speed: startSpeed + (2 * a * u + 3 * b * u * u) / duration,
    acceleration: (2 * a + 6 * b * u) / duration ** 2,
  };
}

function rankOffset(story, beat, rank) {
  if (beat === 0) return STARTING_GRID[rank].distance;
  if (beat === 7) return story.finishOffsets[rank];
  if (beat === 6 && story.family === 'three-way') return [0, -.35, -.7, -12, -20][rank];
  if (beat === 6 && ['photo-finish', 'corner-duel', 'sprint-duel'].includes(story.family)) return story.finishOffsets[rank];
  return runningOffsets[rank] * story.spreads[beat];
}

function packLanes(cars, baseDistance, time, duration) {
  // Keep lateral order stable, but use available longitudinal clearance to tuck
  // into the pack. Spacing grows before cars draw level, not after contact.
  const ordered = [...cars].sort((a, b) => runningLanes[a.gridRank] - runningLanes[b.gridRank]);
  const positions = [];
  ordered.forEach((car, index) => {
    let x = 0;
    for (let before = 0; before < index; before++) {
      const gap = Math.abs(car.distance - ordered[before].distance);
      const finishSpread = smooth(clamp((time - duration + 5) / 2));
      const clearance = .45 + 2.4 * Math.max(smooth(clamp((12 - gap) / 7)), finishSpread);
      x = Math.max(x, positions[before] + clearance);
    }
    positions.push(x);
  });
  const center = positions.at(-1) / 2;
  const cornerLine = -1.1 * smooth(clamp((baseDistance - 180) / 95))
    + 2.2 * smooth(clamp((baseDistance - STRAIGHT_LENGTH) / 110))
    - 1.1 * smooth(clamp((baseDistance - curveEnd) / 100));
  const blend = smooth(clamp((time - 4) / 1.8));
  ordered.forEach((car, index) => {
    const target = positions[index] - center + cornerLine;
    car.lane += (target - car.lane) * blend;
  });
}

function battleEvent(story, round, time, fromBeat, toBeat, blend, order) {
  if (toBeat === fromBeat || time >= story.duration || time < 4.35) return null;
  const from = story.orders[fromBeat], to = story.orders[toBeat];
  if (from.join('') === to.join('')) return null;
  const start = story.beatTimes[fromBeat], end = story.beatTimes[toBeat];
  if (time < start + (end - start) * .18 || time > Math.min(end, start + (end - start) * .18 + 2.6)) return null;
  const attacker = from[0] !== to[0] ? to[0] : to.reduce((best, role) =>
    from.indexOf(role) - to.indexOf(role) > from.indexOf(best) - to.indexOf(best) ? role : best, to[0]);
  const rival = from[0] === attacker ? from[1] : from[0];
  const kinds = ['', 'launchAttack', 'brakingAttack', 'cornerAttack', 'cornerDuel', 'exitAttack', 'sprintAttack', 'photoBattle'];
  let kind = kinds[toBeat];
  if (story.family === 'three-way' && toBeat >= 5) kind = 'threeWay';
  if (story.family === 'comeback' && toBeat >= 2 && toBeat <= 5) kind = 'recovery';
  if (story.family === 'breakthrough' && toBeat === 5) kind = 'breakthrough';
  return { id: `${story.id}:${toBeat}`, kind, carId: round.roles[attacker],
    focusIds: [round.roles[attacker], round.roles[rival]],
    completed: blend > .5 && order[0] === round.roles[attacker] };
}

export function sampleStoryRace(round, elapsedSeconds) {
  const time = Number.isFinite(elapsedSeconds) ? Math.max(0, elapsedSeconds) : 0;
  const story = SCENARIOS[round.scenarioIndex], base = progress(story, time);
  let fromBeat = 0, toBeat = 0, blend = 0, blendRate = 0, blendAcceleration = 0;
  for (let index = 1; index < story.beatTimes.length; index++) {
    const start = story.beatTimes[index - 1];
    if (time > start) {
      fromBeat = index - 1; toBeat = index;
      const duration = story.beatTimes[index] - start, u = clamp((time - start) / duration);
      blend = smooth(u); blendRate = u < 1 ? 6 * u * (1 - u) / duration : 0;
      blendAcceleration = u < 1 ? 6 * (1 - 2 * u) / duration ** 2 : 0;
    }
  }
  const spread = smooth(clamp(time / 1.9)), gridBlend = clamp((time - 1.9) / 2.1);
  const cars = CARS.map(car => {
    const role = round.roles.indexOf(car.id), gridRank = round.gridOrder.indexOf(car.id);
    const initialRank = story.orders[0].indexOf(role);
    const from = rankOffset(story, fromBeat, story.orders[fromBeat].indexOf(role));
    const change = rankOffset(story, toBeat, story.orders[toBeat].indexOf(role)) - from;
    let offset = from + change * blend, rate = change * blendRate, acceleration = change * blendAcceleration;
    if (time <= 4) {
      const gridChange = STARTING_GRID[initialRank].distance - STARTING_GRID[gridRank].distance;
      offset = STARTING_GRID[gridRank].distance + gridChange * smooth(gridBlend);
      rate = gridChange * 6 * gridBlend * (1 - gridBlend) / 2.1;
      acceleration = time > 1.9 && time < 4 ? gridChange * 6 * (1 - 2 * gridBlend) / 2.1 ** 2 : 0;
    }
    return { id: car.id, gridRank, distance: base.distance + offset,
      lane: STARTING_GRID[gridRank].lane + (runningLanes[gridRank] - STARTING_GRID[gridRank].lane) * spread,
      speed: Math.max(0, base.speed + rate), acceleration: base.acceleration + acceleration, rank: 0 };
  });
  if (time > story.duration) {
    for (const car of cars) {
      const rank = round.finalOrder.indexOf(car.id), coast = .8 - rank * .085;
      const decay = Math.exp(-(time - story.duration) / coast);
      car.distance = TRACK_LENGTH + story.finishOffsets[rank] + 60 * coast * (1 - decay);
      car.speed = 60 * decay;
      car.acceleration = -car.speed / coast;
    }
  }
  packLanes(cars, base.distance, time, story.duration);
  const order = [...cars].sort((a, b) => b.distance - a.distance || a.id.localeCompare(b.id)).map(car => car.id);
  for (const car of cars) { car.rank = order.indexOf(car.id) + 1; delete car.gridRank; }
  const leadingDistance = cars.find(car => car.id === order[0]).distance;
  const phase = time >= story.duration ? 'finished' : leadingDistance > TRACK_LENGTH - 210 ? 'sprint'
    : leadingDistance > STRAIGHT_LENGTH && leadingDistance < curveEnd ? 'corner' : time < 7 ? 'launch' : 'straight';
  const event = battleEvent(story, round, time, fromBeat, toBeat, blend, order);
  return { cars, order, phase, time, duration: story.duration, event,
    focusIds: event?.focusIds ?? order.slice(0, 2),
    braking: time < 9 && base.acceleration < -2,
    accelerating: leadingDistance >= curveEnd && leadingDistance < curveEnd + 160,
    leader: order[0], finished: time >= story.duration };
}
